import type {
    MaxCount,
    MaxSizeByte,
    UploadFileType,
    UploadFileTypeOption,
    UploadOptions,
    UploadSizeCategory,
} from '../types/file';
import {UploadError} from '../types/file';

/** 提取文件后缀名（小写，不含点号） */
export function getFileExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === fileName.length - 1) {
        return '';
    }
    return fileName.slice(dotIndex + 1).toLowerCase();
}

const FILE_SIZE_CATEGORY_EXTENSIONS: Record<UploadSizeCategory, string[]> = {
    image: ['jpg', 'jpeg', 'png', 'webp'],
    video: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
    audio: ['mp3', 'wav'],
    txt: ['txt', 'doc', 'docx', 'xlsx'],
};

const FILE_TYPE_BY_SIZE_CATEGORY: Record<UploadSizeCategory, UploadFileType> = {
    image: 'IMAGE',
    video: 'VIDEO',
    audio: 'AUDIO',
    txt: 'TXT',
};

const FILE_TYPE_TO_SIZE_CATEGORY: Record<UploadFileType, UploadSizeCategory> = {
    IMAGE: 'image',
    VIDEO: 'video',
    AUDIO: 'audio',
    TXT: 'txt',
};

/** 根据文件后缀识别大小限制分组 */
export function getUploadSizeCategory(file: File): UploadSizeCategory | null {
    const ext = getFileExtension(file.name);
    const entry = Object.entries(FILE_SIZE_CATEGORY_EXTENSIONS).find(([, extensions]) =>
        extensions.includes(ext),
    );
    return entry ? (entry[0] as UploadSizeCategory) : null;
}

/** 根据 MIME 优先、后缀兜底推断上传文件类型 */
export function inferUploadFileType(file: File): UploadFileType | null {
    if (file.type.startsWith('image/')) {
        return 'IMAGE';
    }
    if (file.type.startsWith('video/')) {
        return 'VIDEO';
    }
    if (file.type.startsWith('audio/')) {
        return 'AUDIO';
    }

    const category = getUploadSizeCategory(file);
    return category ? FILE_TYPE_BY_SIZE_CATEGORY[category] : null;
}

/** 解析当前文件实际适用的最大字节数 */
export function resolveMaxSizeByte(
    file: File,
    maxSizeByte?: MaxSizeByte,
    fileType?: UploadFileType,
): number | undefined {
    let resolvedMaxSizeByte: number | null = null;

    if (typeof maxSizeByte === 'number') {
        resolvedMaxSizeByte = maxSizeByte;
    } else if (maxSizeByte) {
        const resolvedType = fileType ?? inferUploadFileType(file);
        const category = resolvedType
            ? FILE_TYPE_TO_SIZE_CATEGORY[resolvedType]
            : getUploadSizeCategory(file);
        resolvedMaxSizeByte = category ? (maxSizeByte[category] ?? null) : null;
    }

    return resolvedMaxSizeByte ?? undefined;
}

function validateMaxCount(
    files: File[],
    maxCount: MaxCount,
    type: UploadFileTypeOption,
): UploadError | null {
    if (typeof maxCount === 'number') {
        return files.length > maxCount ? new UploadError('VALIDATE', 'FILE_COUNT_EXCEEDED') : null;
    }

    if (type !== 'AUTO') {
        return new UploadError('VALIDATE', 'FILE_COUNT_EXCEEDED');
    }

    const total =
        maxCount.total ??
        Object.entries(maxCount).reduce(
            (sum, [category, count]) =>
                category === 'total' || typeof count !== 'number' ? sum : sum + count,
            0,
        );
    if (files.length > total) {
        return new UploadError('VALIDATE', 'FILE_COUNT_EXCEEDED');
    }

    const counts: Partial<Record<UploadSizeCategory, number>> = {};
    for (const file of files) {
        const resolvedFileType = inferUploadFileType(file);
        if (!resolvedFileType) {
            continue;
        }

        const category = FILE_TYPE_TO_SIZE_CATEGORY[resolvedFileType];
        counts[category] = (counts[category] ?? 0) + 1;
        if ((counts[category] ?? 0) > (maxCount[category] ?? 0)) {
            return new UploadError('VALIDATE', 'FILE_COUNT_EXCEEDED');
        }
    }

    return null;
}

function validateSingleFile(
    file: File,
    allowedExtensions: string[],
    maxSizeByte?: MaxSizeByte,
): UploadError | null {
    // 空文件
    if (file.size === 0) {
        return new UploadError('VALIDATE', 'FILE_EMPTY');
    }

    // 类型校验（后缀名比对，忽略大小写）
    if (allowedExtensions.length > 0) {
        const ext = getFileExtension(file.name);
        const allowed = allowedExtensions.map((e) => e.toLowerCase());
        if (!allowed.includes(ext)) {
            return new UploadError('VALIDATE', 'FILE_TYPE_INVALID');
        }
    }

    // 大小校验
    const resolvedFileType = inferUploadFileType(file) ?? undefined;
    const resolvedMaxSizeByte = resolveMaxSizeByte(file, maxSizeByte, resolvedFileType);
    if (resolvedMaxSizeByte !== undefined && file.size > resolvedMaxSizeByte) {
        return new UploadError('VALIDATE', 'FILE_SIZE_EXCEEDED');
    }

    return null;
}

/**
 * 校验文件列表，返回第一个校验失败的 UploadError，全部通过返回 null
 */
export function validateFiles(
    files: File[],
    options: UploadOptions,
    type: UploadFileTypeOption,
): UploadError | null {
    const {maxCount = 1, allowedExtensions = [], maxSizeByte} = options;

    // 1. 数量校验
    const countError = validateMaxCount(files, maxCount, type);
    if (countError) {
        return countError;
    }

    // 2. 逐文件校验
    for (const file of files) {
        const fileError = validateSingleFile(file, allowedExtensions, maxSizeByte);
        if (fileError) {
            return fileError;
        }
    }

    return null;
}

/** 校验下载 URL 合法性，仅允许 http/https 协议 */
export function isValidDownloadUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
        return false;
    }
}
